import PropTypes from 'prop-types';
import { forwardRef, useMemo, useRef } from 'react';
import { PAGE_TYPE, postFlowFormData } from '@icp/page-renderer-core';
import { Heartbeat } from '@icp/form-renderer-core';
import { useForkRef } from '@icp/hooks';
import FormRendererSelector from '../FormRendererSelector';

const FormNodeFormRenderer = forwardRef(function FormNodeFormRender(props, ref) {
  const {
    pbcToken,
    flowToken,
    flowDefinitionId,
    flowInstanceId,
    threadId,
    instanceData,
    context: contextProp,
    heartbeat,
    FormRendererProps,
  } = props;

  const {
    data: defaultData,
    formEntityDataId,
    formEntityId,
    layoutId,
    pbcToken: formPbcToken,
    formEntityToken,
    layoutToken,
  } = instanceData;

  const formApi = useRef(null);
  const handleRef = useForkRef(ref, formApi);

  // context must memo, every context change will trigger FormRender make new store
  // EditableTableElement 里需要用到 formEntityId formEntityDataId
  const context = useMemo(() => {
    return {
      ...contextProp,
      pbcToken: formPbcToken,
      formEntityId,
      formEntityDataId,
      pageType:
        (flowInstanceId && PAGE_TYPE.flowEdit) ||
        (formEntityDataId && PAGE_TYPE.flowCreateByEditForm) ||
        PAGE_TYPE.flowCreate,
    };
  }, [contextProp, formPbcToken, formEntityId, formEntityDataId, flowInstanceId]);

  const handleSave = (data) => {
    return postFlowFormData({
      heartbeat,
      pbcToken,
      flowToken,
      formPbcToken,
      formEntityToken,
      layoutToken,
      flowDefinitionId,
      flowInstanceId,
      threadId,
      layoutId,
      saveOnly: true,
      data,
    }).then(
      (response) => {
        // 不能 return data 而是调用 get 一下表单的当前数据的原因是因为 Upload 组件、EditableTable 组件等提交
        // 的数据和组件数据不一致，直接 return 提交的数据会导致组件报错
        const oldData = formApi.current.getData();
        return { ...oldData, ...response };
      },
      (errors) => {
        return Promise.reject(errors);
      },
    );
  };

  const handleSubmit = (data) => {
    return postFlowFormData({
      heartbeat,
      pbcToken,
      flowToken,
      formPbcToken,
      formEntityToken,
      layoutToken,
      flowDefinitionId,
      flowInstanceId,
      threadId,
      data,
      layoutId,
    }).then(
      (response) => {
        const oldData = formApi.current.getData();
        return { ...oldData, ...response };
      },
      (errors) => {
        return Promise.reject(errors);
      },
    );
  };

  return (
    <div className="flow-form-renderer">
      <FormRendererSelector
        {...FormRendererProps}
        heartbeat={heartbeat}
        formEntityToken={formEntityToken}
        layoutToken={layoutToken}
        layoutId={layoutId}
        defaultData={{
          ...FormRendererProps?.defaultData,
          ...defaultData,
        }}
        context={context}
        onSubmit={handleSubmit}
        onSave={handleSave}
        ref={handleRef}
      />
    </div>
  );
});

FormNodeFormRenderer.propTypes = {
  heartbeat: PropTypes.instanceOf(Heartbeat),
  pbcToken: PropTypes.string,
  flowToken: PropTypes.string,
  // id from params is string
  flowDefinitionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowInstanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  threadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  instanceData: PropTypes.shape({
    data: PropTypes.shape({}),
    formEntityDataId: PropTypes.number,
    formEntityId: PropTypes.number,
    layoutId: PropTypes.number,
    pbcToken: PropTypes.string,
    formEntityToken: PropTypes.string,
    layoutToken: PropTypes.string,
    lock: PropTypes.shape({}),
  }),
  context: PropTypes.shape({}),
  FormRendererProps: PropTypes.shape({
    defaultData: PropTypes.shape({}),
  }),
};

export default FormNodeFormRenderer;
